import React, { useState, useRef } from 'react';
import {
    Home, BarChart2, LogOut, ChevronLeft, ChevronRight, Users, Shield,
    Compass, PlusCircle, ChevronsUpDown, Send,
    Database, Settings2, AlertTriangle, ChevronDown, Cpu, Trash2,
    LayoutDashboard, FolderKanban, Upload, Search
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import Modal from '../../components/Modal';
import martechLogo from '../../assets/Asset 2.png';
import apiService from '../../services/apiService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://martechai-backend.onrender.com';

const Sidebar = ({
    user,
    activeView,
    setActiveView,
    isExpanded,
    setIsExpanded,
    isMobileNavOpen,
    onMobileNavClose,
    workspaces,
    activeWorkspace,
    onCreateWorkspace,
    onSwitchWorkspace,
}) => {
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const { refetchUser, logout } = useAuth();
    const canManage = user?.role === 'App Admin' || user?.role === 'Team Manager';
    const fileInputRef = useRef(null);
    const [uploadingWorkspaceId, setUploadingWorkspaceId] = useState(null);

    const handleNavClick = (viewId) => {
        setActiveView(viewId);
        if (onMobileNavClose) {
            onMobileNavClose();
        }
    };

    const handleIconUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !uploadingWorkspaceId) return;

        try {
            await apiService.uploadWorkspaceIcon(uploadingWorkspaceId, file);
            await refetchUser();
        } catch (error) {
            alert(`Error uploading icon: ${error.message || 'Please try again.'}`);
        } finally {
            setUploadingWorkspaceId(null);
        }
    };
    
    const triggerFileInput = (e, workspaceId) => {
        e.stopPropagation();
        setUploadingWorkspaceId(workspaceId);
        fileInputRef.current.click();
    };

    const handleDeleteWorkspace = async (e, workspaceId, workspaceName) => {
        e.stopPropagation();
        if (window.confirm(`Are you sure you want to permanently delete the "${workspaceName}" workspace? This action cannot be undone.`)) {
            try {
                await apiService.deleteWorkspace(workspaceId);
                await refetchUser();
                if (activeWorkspace?._id === workspaceId) {
                    const remainingWorkspaces = workspaces.filter(ws => ws._id !== workspaceId);
                    if (remainingWorkspaces.length > 0) {
                        onSwitchWorkspace(remainingWorkspaces[0]._id);
                    }
                }
            } catch (error) {
                alert(`Error deleting workspace: ${error.message}`);
            }
        }
    };

    // --- CORRECTED NAV ITEMS ---
    const navItems = [
        { id: 'home', label: 'Dashboard', icon: <LayoutDashboard /> },
        { 
            id: 'campaignsParent', // Changed ID to avoid conflict
            label: 'Campaigns', 
            icon: <Cpu />,
            children: [
                { id: 'campaigns', label: 'Campaign Builder' }, // Corrected item
                { id: 'foundryContentCreation', label: 'Content Creation' },
            ]
        },
        {
            id: 'research', 
            label: 'Research', 
            icon: <Search />,
            children: [
                { id: 'insights', label: 'Insights' }, 
                { id: 'foundryCompetitorAnalysis', label: 'Competitor Research' },
                { id: 'seoAudit', label: 'SEO Audit' },
            ]
        },
        { id: 'analytics', label: 'Analytics', icon: <BarChart2 /> },
        { id: 'compass', label: 'Compass', icon: <Compass /> },
        {
            id: 'configuration', 
            label: 'Configuration', 
            icon: <Settings2 />,
            children: [
                { id: 'team', label: 'Team' },
                { id: 'integrations', label: 'Integrations' },
                { id: 'hubspotQueryTester', label: 'HubSpot API Tester' },
                { id: 'dataHub', label: 'Data Hub' },
            ]
        },
        {
            id: 'goingAway', 
            label: 'Going Away', 
            icon: <AlertTriangle />,
            children: [
                { id: 'flightdeck', label: 'Flight Deck' },
            ]
        },
    ];


    if (user?.role === 'App Admin') {
        const configIndex = navItems.findIndex(item => item.id === 'configuration');
        if (configIndex !== -1) {
            navItems.splice(configIndex, 0, { id: 'admin', label: 'Admin', icon: <Shield /> });
        } else {
            navItems.push({ id: 'admin', label: 'Admin', icon: <Shield /> });
        }
    }

    // --- NESTED COMPONENTS ---
    const UserAvatar = ({ user }) => {
        const initial = user?.name ? user.name.charAt(0).toUpperCase() : '?';
        const colorIndex = user?._id ? user._id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % 5 : 0;
        const colors = ['bg-[#16e2c3]', 'bg-cyan-400', 'bg-violet-400', 'bg-pink-400', 'bg-orange-400'];
        return <div className={`w-8 h-8 ${colors[colorIndex]} rounded-full flex items-center justify-center font-bold text-black flex-shrink-0`}>{initial}</div>;
    };
    
    const WorkspaceIcon = ({ workspace }) => {
        if (workspace.iconUrl) {
            return (
                <img 
                    src={`${API_BASE_URL}/api/workspaces/icons/${workspace.iconUrl}`} 
                    alt={`${workspace.name} icon`}
                    className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                />
            );
        }
        const initial = workspace.name ? workspace.name.charAt(0).toUpperCase() : 'W';
        return (
             <div className="w-8 h-8 bg-slate-700 rounded-md flex-shrink-0 flex items-center justify-center text-slate-400 font-bold">
                {initial}
            </div>
        );
    };

    const NavItem = ({ item }) => {
        const hasChildren = item.children && item.children.length > 0;
        const isParentOfActive = hasChildren && item.children.some(child => child.id === activeView);
        const isActive = activeView === item.id || isParentOfActive;
        const [isSubMenuOpen, setIsSubMenuOpen] = useState(isParentOfActive);

        const handleParentClick = (e) => {
            e.preventDefault();
            if (hasChildren) {
                setIsSubMenuOpen(!isSubMenuOpen);
            } else {
                handleNavClick(item.id);
            }
        };
        
        const handleChildClick = (e, child) => {
            e.stopPropagation();
            if (!child.disabled) {
                handleNavClick(child.id);
            }
        };

        return (
            <div className="relative">
                {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 h-3/4 w-1 bg-brand-400 rounded-r-full shadow-[0_0_15px_1px_rgba(167,139,250,0.6)]"></div>
                )}
                <button
                    onClick={handleParentClick}
                    className={`flex items-center gap-3 w-full text-left rounded-lg transition-colors p-3 ${
                        isActive
                        ? 'bg-slate-700/50 text-white'
                        : 'text-slate-300 hover:bg-slate-800'
                    }`}
                >
                    {item.icon ? React.cloneElement(item.icon, { size: 20 }) : null}
                    {isExpanded && <span className="font-semibold flex-grow">{item.label}</span>}
                    {hasChildren && isExpanded && <ChevronDown className={`transition-transform ${isSubMenuOpen ? 'rotate-180' : ''}`} size={16} />}
                </button>
                {hasChildren && isSubMenuOpen && isExpanded && (
                    <div className="mt-2 pl-8 space-y-1">
                        {item.children.map(child => (
                            <button key={child.id} onClick={(e) => handleChildClick(e, child)} className={`flex items-center gap-3 w-full text-left rounded-lg transition-colors p-2 text-sm ${activeView === child.id ? 'text-white font-semibold' : 'text-slate-400 hover:text-white hover:bg-slate-800/50'}`}>
                                <span>{child.label}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    const sidebarClasses = `
        bg-slate-900 border-r border-slate-800 h-screen flex flex-col z-40 transition-all duration-300
        fixed
        ${isExpanded ? 'w-64' : 'w-20'}
        ${isMobileNavOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0
    `;

    return (
        <>
            <input type="file" ref={fileInputRef} onChange={handleIconUpload} className="hidden" accept="image/png, image/jpeg, image/webp" />
            <aside className={sidebarClasses}>
                <div className={`p-4 flex items-center gap-3 border-b border-slate-800 h-[69px] ${isExpanded ? 'justify-start' : 'justify-center'}`}>
                    <img src={martechLogo} alt="MartechAI Logo" className="w-8 h-8 flex-shrink-0" />
                    {isExpanded && <span className="text-xl font-bold text-white">MartechAI</span>}
                </div>

                <div className={`p-2 ${isExpanded ? 'border-b border-slate-800' : ''}`}>
                    <details className="group">
                        <summary className={`flex items-center p-2 rounded-lg cursor-pointer hover:bg-slate-800 list-none ${isExpanded ? '' : 'justify-center'}`}>
                             <div className="flex-grow flex items-center overflow-hidden">
                                <div className="relative group/icon">
                                     <WorkspaceIcon workspace={activeWorkspace || {name: 'W'}} />
                                     <div 
                                        className="absolute inset-0 bg-black/60 rounded-md flex items-center justify-center opacity-0 group-hover/icon:opacity-100 transition-opacity cursor-pointer"
                                        onClick={(e) => triggerFileInput(e, activeWorkspace?._id)}
                                     >
                                         <Upload size={16} className="text-white"/>
                                     </div>
                                </div>
                                {isExpanded && (
                                    <div className="text-left ml-3">
                                        <p className="font-semibold text-white">{activeWorkspace?.name || 'Workspace'}</p>
                                        <p className="text-xs text-slate-400">Select Workspace</p>
                                    </div>
                                )}
                            </div>
                            {isExpanded && <ChevronsUpDown size={16} className="text-slate-400 ml-auto flex-shrink-0"/>}
                        </summary>
                        {isExpanded && (
                            <div className="mt-2 space-y-1">
                                {(workspaces || []).map(ws => (
                                     <a key={ws._id} href="#" onClick={() => onSwitchWorkspace(ws._id)} className={`w-full flex justify-between items-center p-3 text-sm rounded-lg group/item ${activeWorkspace && ws._id === activeWorkspace._id ? 'bg-slate-700/50 text-white' : 'text-gray-400 hover:bg-slate-800'}`}>
                                        <span className="flex items-center gap-3">
                                            <WorkspaceIcon workspace={ws} />
                                            <span className="flex-1">{ws.name}</span>
                                        </span>
                                        {canManage && (
                                            <button onClick={(e) => handleDeleteWorkspace(e, ws._id, ws.name)} className="text-slate-500 hover:text-red-400 opacity-0 group-hover/item:opacity-100 p-1">
                                                <Trash2 size={14} />
                                            </button>
                                        )}
                                    </a>
                                ))}
                                {canManage && (
                                    <button onClick={onCreateWorkspace} className="w-full flex items-center p-3 text-sm rounded-lg text-gray-400 hover:bg-slate-800">
                                        <PlusCircle className="w-5 h-5 flex-shrink-0" />
                                        <span className="ml-4 flex-1 text-left">New Workspace</span>
                                    </button>
                                )}
                            </div>
                        )}
                    </details>
                </div>

                <nav className="flex-grow p-2 space-y-1 overflow-y-auto">
                    {navItems.map(item => {
                        if (item.id === 'configuration' && !canManage) {
                            return null;
                        }
                        return <NavItem key={item.id} item={item} />;
                    })}
                </nav>

                <div className="p-2 border-t border-slate-800">
                     <button onClick={() => handleNavClick('settings')} className={`w-full flex items-center gap-3 p-2 rounded-lg ${isExpanded ? 'justify-start' : 'justify-center'} hover:bg-slate-800`}>
                        <UserAvatar user={user} />
                        {isExpanded && <div className="text-left"><p className="font-semibold text-white">{user?.name}</p><p className="text-xs text-slate-400">{user?.email}</p></div>}
                    </button>
                    <button onClick={() => setIsLogoutModalOpen(true)} className={`w-full flex items-center gap-3 p-3 rounded-lg mt-1 ${isExpanded ? 'justify-start' : 'justify-center'} text-slate-400 hover:bg-slate-800 hover:text-red-400`}>
                        <LogOut size={20} />
                        {isExpanded && <span className="font-semibold">Logout</span>}
                    </button>
                </div>
                
                <button onClick={() => setIsExpanded(!isExpanded)} className="absolute -right-3 top-20 hidden md:block -translate-y-1/2 bg-slate-700 hover:bg-brand-500 text-slate-300 hover:text-white p-1.5 rounded-full transition-colors z-50">
                    {isExpanded ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
                </button>
            </aside>

            {isMobileNavOpen && (
                <div className="fixed inset-0 bg-black/60 z-30 md:hidden" onClick={onMobileNavClose}></div>
            )}
            <LogoutModal isOpen={isLogoutModalOpen} onClose={() => setIsLogoutModalOpen(false)} logout={logout} />
        </>
    );
};

const LogoutModal = ({ isOpen, onClose, logout }) => {
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    
    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            await logout();
            window.location.reload();
        } catch (error) {
            console.error("Logout failed:", error);
            alert("Logout failed. Please try again.");
            setIsLoggingOut(false);
        }
    };
    if (!isOpen) return null;
    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Confirm Logout">
            <div className="text-center">
                <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
                <p className="mt-4 text-slate-300">Are you sure you want to log out?</p>
            </div>
            <div className="mt-6 flex justify-end gap-3">
                <button type="button" onClick={onClose} className="bg-slate-600 text-white font-bold py-2 px-4 rounded-md hover:bg-slate-500" disabled={isLoggingOut}>
                    Cancel
                </button>
                <button onClick={handleLogout} disabled={isLoggingOut} className="bg-red-500 text-white font-bold py-2 px-4 rounded-md hover:bg-red-600 inline-flex items-center justify-center disabled:opacity-50">
                    {isLoggingOut ? 'Logging out...' : 'Yes, Logout'}
                </button>
            </div>
        </Modal>
    );
};


export default Sidebar;